import { useEffect, type RefObject } from "react";
import { useTaskPocketStore } from "@/stores/task-pocket-store";

/** Menus, the schedule form and the image lightbox the pocket opens outside its DOM. */
const OWNED_SURFACE = "[data-task-pocket-owned]";
const EDITABLE = "input, textarea, [contenteditable='true']";

/**
 * Escape or a pointer down outside closes the open task pocket (spec §6.5).
 * While a surface the pocket owns is open, clicks and Escape belong to it.
 * Escape is handled in the capture phase and stopped there, so the global
 * Escape handler that stops the running agent never sees it; fields inside
 * the pocket get Escape first, and the pocket's own key handler closes it
 * when they leave the key alone.
 */
export function useTaskPocketDismiss(
  pocketRef: RefObject<HTMLElement | null>,
  active: boolean,
): void {
  useEffect(() => {
    if (!active) return;
    const close = () => useTaskPocketStore.getState().close();
    const ownedSurfaceOpen = () =>
      document.querySelector(OWNED_SURFACE) !== null;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (
        !target ||
        pocketRef.current?.contains(target) ||
        ownedSurfaceOpen()
      ) {
        return;
      }
      if (target.closest("[data-task-pocket-item]")) return;
      const focused = document.activeElement;
      if (
        focused instanceof HTMLElement &&
        pocketRef.current?.contains(focused)
      )
        focused.blur();
      close();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (ownedSurfaceOpen()) return;
      const target = event.target instanceof Element ? event.target : null;
      if (
        target &&
        pocketRef.current?.contains(target) &&
        target.closest(EDITABLE)
      ) {
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      close();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [active, pocketRef]);
}
